import React, { useState, useEffect } from 'react';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Milestone, Plus, Trash2, Calendar, Target, CheckCircle, Clock, 
  ChevronDown, ChevronRight, X, Check, Edit2, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  planned: { color: 'bg-slate-500', label: 'Planned', textColor: 'text-slate-400' },
  in_progress: { color: 'bg-blue-500', label: 'In Progress', textColor: 'text-blue-400' },
  completed: { color: 'bg-emerald-500', label: 'Completed', textColor: 'text-emerald-400' },
};

const epicColors = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', 
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
];

export default function RoadmapPanel({ projectId, tasks = [] }) {
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedEpic, setExpandedEpic] = useState(null);
  const [editingEpic, setEditingEpic] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, timeline

  const [newEpic, setNewEpic] = useState({
    title: '', description: '', status: 'planned', target_date: '', color: epicColors[0]
  });

  useEffect(() => {
    loadEpics();
  }, [projectId]);

  const loadEpics = async () => {
    try {
      const res = await projectAPI.listEpics(projectId);
      setEpics(res.data);
    } catch (err) {
      toast.error('Failed to load epics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEpic = async () => {
    if (!newEpic.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await projectAPI.createEpic(projectId, newEpic);
      setNewEpic({ title: '', description: '', status: 'planned', target_date: '', color: epicColors[Math.floor(Math.random() * epicColors.length)] });
      setShowAddForm(false);
      loadEpics();
      toast.success('Epic created');
    } catch (err) {
      toast.error('Failed to create epic');
    }
  };

  const handleUpdateEpic = async (epicId, updates) => {
    try {
      await projectAPI.updateEpic(projectId, epicId, updates);
      loadEpics();
      setEditingEpic(null);
      toast.success('Epic updated');
    } catch (err) {
      toast.error('Failed to update epic');
    }
  };

  const handleDeleteEpic = async (epicId) => {
    try {
      await projectAPI.deleteEpic(projectId, epicId);
      loadEpics();
      toast.success('Epic deleted');
    } catch (err) {
      toast.error('Failed to delete epic');
    }
  };

  const getEpicTasks = (epicId) => tasks.filter(t => t.epic_id === epicId);

  const getProgress = (epic) => {
    const epicTasks = getEpicTasks(epic.id);
    if (epicTasks.length === 0) return 0;
    const done = epicTasks.filter(t => t.status === 'done').length;
    return Math.round((done / epicTasks.length) * 100);
  };

  const totalStats = {
    planned: epics.filter(e => e.status === 'planned').length,
    in_progress: epics.filter(e => e.status === 'in_progress').length,
    completed: epics.filter(e => e.status === 'completed').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-7 w-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2 flex-wrap">
        <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
          <Milestone className="w-4 h-4 mr-1" /> Roadmap & Epics
        </Badge>
        
        {/* Stats */}
        <div className="flex items-center gap-2 ml-2">
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-slate-500/5 border border-slate-500/10">
            <span className="opacity-50">Planned:</span>
            <span className="font-medium">{totalStats.planned}</span>
          </div>
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-500/5 border border-blue-500/10">
            <span className="opacity-50">Active:</span>
            <span className="font-medium text-blue-400">{totalStats.in_progress}</span>
          </div>
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-500/5 border border-emerald-500/10">
            <span className="opacity-50">Done:</span>
            <span className="font-medium text-emerald-400">{totalStats.completed}</span>
          </div>
        </div>
        
        <div className="flex-1" />
        
        <div className="flex items-center gap-1">
          <Button size="sm" variant={viewMode === 'list' ? 'secondary' : 'ghost'} className="h-8 text-xs px-2" onClick={() => setViewMode('list')} data-testid="roadmap-list-view">
            <Target className="w-4 h-4 mr-1" /> List
          </Button>
          <Button size="sm" variant={viewMode === 'timeline' ? 'secondary' : 'ghost'} className="h-8 text-xs px-2" onClick={() => setViewMode('timeline')} data-testid="roadmap-timeline-view">
            <BarChart3 className="w-4 h-4 mr-1" /> Timeline
          </Button>
        </div>
        
        <Button size="sm" className="h-8 text-xs bg-purple-500 hover:bg-purple-600 text-white" onClick={() => setShowAddForm(true)} data-testid="add-epic-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Epic
        </Button>
      </div>

      {/* Add Epic Form */}
      {showAddForm && (
        <div className="p-3 border-b border-[var(--surface-border)] bg-[var(--surface-card)]">
          <div className="grid grid-cols-2 gap-3 max-w-2xl">
            <div className="col-span-2">
              <Label className="text-xs mb-1 block opacity-60">Epic Title *</Label>
              <Input value={newEpic.title} onChange={(e) => setNewEpic(ep => ({ ...ep, title: e.target.value }))} placeholder="e.g., User Authentication, Payment Integration" className="h-8 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-epic-title" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs mb-1 block opacity-60">Description</Label>
              <Textarea value={newEpic.description} onChange={(e) => setNewEpic(ep => ({ ...ep, description: e.target.value }))} placeholder="Goals and objectives..." className="text-sm bg-transparent border-[var(--surface-border)] min-h-[60px]" data-testid="new-epic-desc" />
            </div>
            <div>
              <Label className="text-xs mb-1 block opacity-60">Status</Label>
              <Select value={newEpic.status} onValueChange={(v) => setNewEpic(ep => ({ ...ep, status: v }))}>
                <SelectTrigger className="h-8 text-xs bg-transparent border-[var(--surface-border)]" data-testid="new-epic-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block opacity-60">Target Date</Label>
              <Input type="date" value={newEpic.target_date} onChange={(e) => setNewEpic(ep => ({ ...ep, target_date: e.target.value }))} className="h-8 text-xs bg-transparent border-[var(--surface-border)]" data-testid="new-epic-date" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs mb-1 block opacity-60">Color</Label>
              <div className="flex gap-2">
                {epicColors.map(color => (
                  <button key={color} className={`w-6 h-7 rounded-full border-2 transition-all ${newEpic.color === color ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`} style={{ backgroundColor: color }} onClick={() => setNewEpic(ep => ({ ...ep, color }))} data-testid={`color-${color}`} />
                ))}
              </div>
            </div>
            <div className="col-span-2 flex gap-2">
              <Button size="sm" className="h-8 text-xs bg-purple-500 hover:bg-purple-600 text-white" onClick={handleCreateEpic} data-testid="create-epic-btn">
                <Check className="w-4 h-4 mr-1" /> Create Epic
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowAddForm(false)} data-testid="cancel-epic-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-3">
            {epics.map(epic => {
              const isExpanded = expandedEpic === epic.id;
              const epicTasks = getEpicTasks(epic.id);
              const progress = getProgress(epic);
              
              return (
                <div key={epic.id} className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] overflow-hidden" data-testid={`epic-card-${epic.id}`}>
                  <div className="p-3 flex items-start gap-3 cursor-pointer hover:bg-[var(--surface-bg)]/50" onClick={() => setExpandedEpic(isExpanded ? null : epic.id)}>
                    <div className="w-1.5 self-stretch rounded-full" style={{ backgroundColor: epic.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="w-4 h-4 opacity-40" /> : <ChevronRight className="w-4 h-4 opacity-40" />}
                        <p className="text-sm font-medium">{epic.title}</p>
                        <Badge className={`text-xs h-4 ${statusConfig[epic.status]?.color}/10 ${statusConfig[epic.status]?.textColor} border-${statusConfig[epic.status]?.color}/20`}>
                          {statusConfig[epic.status]?.label}
                        </Badge>
                      </div>
                      {epic.description && !isExpanded && <p className="text-xs opacity-50 line-clamp-1 mt-0.5 ml-5">{epic.description}</p>}
                      
                      <div className="flex items-center gap-4 mt-2 ml-5">
                        {/* Progress Bar */}
                        <div className="flex items-center gap-2 flex-1 max-w-xs">
                          <div className="flex-1 h-1.5 bg-[var(--surface-border)] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: epic.color }} />
                          </div>
                          <span className="text-xs opacity-50 w-8">{progress}%</span>
                        </div>
                        
                        {/* Task Count */}
                        <div className="flex items-center gap-1 text-xs opacity-50">
                          <CheckCircle className="w-4 h-4" />
                          {epicTasks.filter(t => t.status === 'done').length}/{epicTasks.length} tasks
                        </div>
                        
                        {/* Target Date */}
                        {epic.target_date && (
                          <div className="flex items-center gap-1 text-xs opacity-50">
                            <Calendar className="w-4 h-4" />
                            {new Date(epic.target_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Select value={epic.status} onValueChange={(v) => handleUpdateEpic(epic.id, { ...epic, status: v })} onClick={(e) => e.stopPropagation()}>
                        <SelectTrigger className="h-7 text-xs w-24 bg-transparent border-[var(--surface-border)]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                          {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-7 w-6 opacity-30 hover:opacity-100 hover:text-red-400" onClick={(e) => { e.stopPropagation(); handleDeleteEpic(epic.id); }} data-testid={`delete-epic-${epic.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-[var(--surface-border)] bg-[var(--surface-bg)]/30">
                      {epic.description && (
                        <div className="mt-3">
                          <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-1">Description</p>
                          <p className="text-xs opacity-70">{epic.description}</p>
                        </div>
                      )}
                      
                      {/* Tasks in Epic */}
                      <div className="mt-4">
                        <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-2">Tasks ({epicTasks.length})</p>
                        {epicTasks.length > 0 ? (
                          <div className="space-y-1">
                            {epicTasks.map(task => (
                              <div key={task.id} className="flex items-center gap-2 px-2 py-1.5 bg-[var(--surface-card)] rounded border border-[var(--surface-border)]">
                                <div className={`w-1.5 h-1.5 rounded-full ${task.status === 'done' ? 'bg-emerald-400' : task.status === 'in_progress' ? 'bg-blue-400' : 'bg-slate-400'}`} />
                                <span className="text-xs flex-1 truncate">{task.title}</span>
                                <Badge variant="outline" className="text-xs h-3.5">{task.status}</Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs opacity-40 py-4 text-center">No tasks assigned to this epic</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {epics.length === 0 && (
              <div className="py-16 text-center">
                <Milestone className="w-10 h-10 opacity-10 mx-auto mb-2" />
                <p className="opacity-30 text-sm">No epics created yet</p>
                <p className="opacity-20 text-xs mt-1">Create epics to organize your roadmap</p>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <ScrollArea className="flex-1">
          <div className="p-4">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[var(--surface-border)]" />
              
              <div className="space-y-6">
                {epics.map((epic, idx) => {
                  const progress = getProgress(epic);
                  const epicTasks = getEpicTasks(epic.id);
                  
                  return (
                    <div key={epic.id} className="relative pl-10" data-testid={`timeline-epic-${epic.id}`}>
                      {/* Timeline Dot */}
                      <div className="absolute left-2 w-5 h-5 rounded-full border-2 border-[var(--surface-bg)] flex items-center justify-center" style={{ backgroundColor: epic.color }}>
                        {epic.status === 'completed' ? <CheckCircle className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-white" />}
                      </div>
                      
                      <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium">{epic.title}</p>
                            {epic.description && <p className="text-xs opacity-50 mt-0.5">{epic.description}</p>}
                          </div>
                          <Badge className={`text-xs ${statusConfig[epic.status]?.color}/10 ${statusConfig[epic.status]?.textColor}`}>
                            {statusConfig[epic.status]?.label}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex-1 h-1.5 bg-[var(--surface-border)] rounded-full overflow-hidden max-w-xs">
                            <div className="h-full rounded-full" style={{ width: `${progress}%`, backgroundColor: epic.color }} />
                          </div>
                          <span className="text-xs opacity-50">{progress}%</span>
                          <span className="text-xs opacity-50">{epicTasks.filter(t => t.status === 'done').length}/{epicTasks.length} tasks</span>
                          {epic.target_date && (
                            <span className="text-xs opacity-50 flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(epic.target_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {epics.length === 0 && (
                  <div className="py-16 text-center pl-10">
                    <Milestone className="w-10 h-10 opacity-10 mx-auto mb-2" />
                    <p className="opacity-30 text-sm">No epics in timeline</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
